import { useEffect, useMemo, useState } from 'react';
import { FileText, ExternalLink, AlertTriangle, FileClock, Loader2 } from 'lucide-react';
import { vatReportingApi } from '../../services/vatReportingApi';
import { getBcPostedDocumentUrl, bcLinkHint } from '../../utils/bcLinks';
import { SortableHeader, nextSort, type SortState } from '../ui/SortableHeader';
import { ColumnFilter } from '../ui/ColumnFilter';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { useColumnFilters } from '../../hooks/useColumnFilters';
import { useVatLang } from './VatLang';
import { EntryExclusionDialog, type ExclusionTarget } from './EntryExclusionDialog';
import { ReportingAmountCell } from './AmountOverrideDialog';
import {
    ExclusionActionCell, exclusionRowClass, exclusionTargetFor, useExclusionTitle,
} from './exclusionRow';
import type {
    ExclusionAction, SnapshotDelta, VatCompany, VatEntryRow, VatPeriod,
} from '../../types/vatReporting';

/**
 * "Mehrwertsteuer Posten" - the full transaction basis the calculation stands on, prefiltered
 * to the page's company/year/month. Reuses the same table standard, column filter and document
 * links as `EntryDrillModal`, but as a full tab rather than a modal, and over the WHOLE month
 * instead of one figure's evidence (`scope=all` on the entries endpoint).
 *
 * TWO SOURCES, ONE TABLE. A filed period offers a toggle: the frozen list (what was actually
 * filed on, from `VatReturnPeriodEntry`) or the current data (the live replica, same endpoint
 * every other panel drills into). Both come back shaped as `VatEntryRow` - a frozen row simply
 * carries null in the handful of fields migration 166 never captured (see the type). Rows that
 * differ between the two are marked in the colour `loadSnapshotDelta` already assigns them:
 * added (only visible live), changed (visible on both sides, with each side's own figure) and
 * removed (only visible in the frozen list - it no longer exists in the replica at all).
 */

interface Props {
    company: VatCompany;
    year: number;
    month: number;
    canEdit: boolean;
    onError: (message: string) => void;
}

const money = (v: number | null, dp = 2) => v === null || v === undefined
    ? '—'
    : new Intl.NumberFormat('de-CH', { minimumFractionDigits: dp, maximumFractionDigits: dp }).format(v);

type ColKey =
    | 'entryNo' | 'reportingDate' | 'postingDate' | 'documentNo' | 'documentType' | 'counterparty'
    | 'counterpartyVatId' | 'countryRegionCode' | 'groups' | 'kennzahl' | 'disposition'
    | 'baseLocal' | 'taxLocal' | 'baseReporting' | 'taxReporting' | 'settlement';

const COLUMNS: { key: ColKey; labelKey: string; width: number; align?: 'right' }[] = [
    { key: 'entryNo', labelKey: 'drill.entry', width: 80 },
    { key: 'reportingDate', labelKey: 'entries.reportingDate', width: 100 },
    { key: 'postingDate', labelKey: 'entries.postingDate', width: 100 },
    { key: 'documentNo', labelKey: 'drill.document', width: 130 },
    { key: 'documentType', labelKey: 'drill.type', width: 100 },
    { key: 'counterparty', labelKey: 'drill.counterparty', width: 200 },
    { key: 'counterpartyVatId', labelKey: 'drill.partnerVatId', width: 130 },
    { key: 'countryRegionCode', labelKey: 'entries.country', width: 70 },
    { key: 'groups', labelKey: 'drill.groups', width: 190 },
    { key: 'kennzahl', labelKey: 'ustva.kz', width: 60 },
    { key: 'disposition', labelKey: 'entries.disposition', width: 100 },
    { key: 'baseLocal', labelKey: 'drill.baseLocal', width: 110, align: 'right' },
    { key: 'taxLocal', labelKey: 'drill.taxLocal', width: 100, align: 'right' },
    { key: 'baseReporting', labelKey: 'drill.baseReporting', width: 110, align: 'right' },
    { key: 'taxReporting', labelKey: 'drill.taxReporting', width: 100, align: 'right' },
    { key: 'settlement', labelKey: 'entries.settlementCol', width: 90 },
];

const ACCESSOR: Record<ColKey, (r: VatEntryRow) => string | number | null> = {
    entryNo: r => r.entryNo,
    reportingDate: r => r.reportingDate,
    postingDate: r => r.postingDate,
    documentNo: r => r.documentNo,
    documentType: r => r.documentType,
    counterparty: r => r.counterpartyName ?? r.counterpartyNo,
    counterpartyVatId: r => r.counterpartyVatId,
    countryRegionCode: r => r.countryRegionCode,
    groups: r => `${r.vatBusPostingGroup} / ${r.vatProdPostingGroup}`,
    kennzahl: r => r.kennzahl,
    disposition: r => r.disposition,
    baseLocal: r => r.baseLocal,
    taxLocal: r => r.taxLocal,
    baseReporting: r => r.baseReporting,
    taxReporting: r => r.taxReporting,
    settlement: r => r.kind === 'Ausgleich' ? 1 : 0,
};

const NUMERIC: ColKey[] = ['entryNo', 'baseLocal', 'taxLocal', 'baseReporting', 'taxReporting'];

const FILTER_COLUMNS = COLUMNS.map(c => ({
    key: c.key,
    accessor: ACCESSOR[c.key],
    numeric: NUMERIC.includes(c.key),
}));

type ViewMode = 'current' | 'frozen';

export function VatEntriesTab({ company, year, month, canEdit, onError }: Props) {
    const { t } = useVatLang();
    const [period, setPeriod] = useState<VatPeriod | null>(null);
    const [delta, setDelta] = useState<SnapshotDelta | null>(null);
    const [view, setView] = useState<ViewMode>('current');
    const [includeSettlement, setIncludeSettlement] = useState(false);
    const [rows, setRows] = useState<VatEntryRow[]>([]);
    const [total, setTotal] = useState(0);
    const [truncated, setTruncated] = useState(false);
    const [snapshotAvailable, setSnapshotAvailable] = useState(true);
    const [periodLocked, setPeriodLocked] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [dialog, setDialog] = useState<{ action: ExclusionAction; target: ExclusionTarget } | null>(null);
    const [sort, setSort] = useState<SortState<ColKey>>({ key: 'entryNo', direction: 'asc' });

    const { widthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(COLUMNS, 'vat-entries-col-widths');
    const { filtered, filters, setColumnFilter, isFiltered, activeCount, clearAll, uniqueValuesFor } =
        useColumnFilters(rows, FILTER_COLUMNS);
    const filterLabels = {
        search: t('filter.search'), all: t('filter.all'), none: t('filter.none'),
        from: t('filter.from'), to: t('filter.to'), empty: t('filter.empty'),
    };

    const onSort = (key: ColKey) => {
        if (shouldSuppressClick()) return;
        setSort(s => nextSort(s, key, NUMERIC.includes(key)));
    };

    // The period status and its delta - light enough to fetch once per selection, and it is
    // what decides whether the toggle even appears and which side it defaults to.
    useEffect(() => {
        let cancelled = false;
        vatReportingApi.getReturn({ company, year, month })
            .then(r => {
                if (cancelled) return;
                setPeriod(r.period);
                setDelta(r.delta);
                setView(r.period?.status === 'filed' ? 'frozen' : 'current');
            })
            .catch(() => { /* the entries fetch below reports its own failure */ });
        return () => { cancelled = true; };
    }, [company, year, month]);

    const exclusionTitle = useExclusionTitle();

    const openAction = (action: ExclusionAction, row: VatEntryRow) =>
        setDialog({ action, target: exclusionTargetFor(row) });

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        // `includeExcluded` on the live side and nowhere else. This tab exists to show the
        // COMPLETE basis the calculation stands on, which has to include what was taken out of
        // it by hand - those rows appear struck through with their reason. The frozen side
        // needs nothing: a snapshot only ever contained the postings that were declared.
        const request = view === 'frozen'
            ? vatReportingApi.getSnapshotEntries({ company, year, month })
                .then(r => ({
                    entries: r.entries, total: r.total, truncated: false,
                    available: r.available, periodLocked: true,
                }))
            : vatReportingApi.getEntries({
                company, year, month, scope: 'all', includeSettlement, includeExcluded: true,
            }).then(r => ({
                entries: r.entries, total: r.total, truncated: r.truncated,
                available: true, periodLocked: r.periodLocked,
            }));
        request
            .then(r => {
                if (cancelled) return;
                setRows(r.entries);
                setTotal(r.total);
                setTruncated(r.truncated);
                setSnapshotAvailable(r.available);
                setPeriodLocked(r.periodLocked);
            })
            .catch(e => {
                if (cancelled) return;
                const message = e instanceof Error ? e.message : t('panel.loadFailedEntries');
                setError(message);
                onError(message);
            })
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [company, year, month, view, includeSettlement, reloadKey, onError, t]);

    const deltaByEntry = useMemo(() => {
        const added = new Set<number>();
        const changed = new Map<number, { snapshotBase?: number; snapshotTax?: number; currentBase?: number; currentTax?: number }>();
        const removed = new Set<number>();
        if (delta?.available) {
            for (const e of delta.added) added.add(e.entryNo);
            for (const e of delta.changed) changed.set(e.entryNo, e);
            for (const e of delta.removed) removed.add(e.entryNo);
        }
        return { added, changed, removed };
    }, [delta]);

    const reconstructed = period?.snapshotOrigin === 'reconstructed';
    const canToggle = period?.status === 'filed';

    const sorted = sort.key
        ? [...filtered].sort((a, b) => {
            const acc = ACCESSOR[sort.key as ColKey];
            const av = acc(a);
            const bv = acc(b);
            if (av === null || av === undefined || av === '') return 1;
            if (bv === null || bv === undefined || bv === '') return -1;
            const base = typeof av === 'number' && typeof bv === 'number'
                ? av - bv
                : String(av).localeCompare(String(bv), undefined, { numeric: true });
            return base * (sort.direction === 'asc' ? 1 : -1);
        })
        : filtered;

    const sumBaseLocal = filtered.reduce((s, r) => s + r.baseLocal, 0);
    const sumTaxLocal = filtered.reduce((s, r) => s + r.taxLocal, 0);
    const sumBaseReporting = filtered.reduce((s, r) => s + (r.baseReporting ?? 0), 0);
    const sumTaxReporting = filtered.reduce((s, r) => s + (r.taxReporting ?? 0), 0);
    const anyReporting = filtered.some(r => r.baseReporting !== null);

    return (
        <div className="space-y-3">
            <div className="border rounded-lg bg-white overflow-hidden">
                <div className="px-4 py-2.5 border-b bg-gray-50 flex flex-wrap items-center justify-between gap-3">
                    <h3 className="text-sm font-semibold text-gray-900">
                        {t('tab.entries')}
                        <span className="ml-2 font-normal text-gray-500">
                            {`${company.toUpperCase()} · ${year}-${String(month).padStart(2, '0')}`}
                        </span>
                    </h3>
                    <div className="flex items-center gap-3">
                        {view === 'current' && (
                            <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                                <input type="checkbox" checked={includeSettlement}
                                       onChange={e => setIncludeSettlement(e.target.checked)} />
                                {t('entries.includeSettlement')}
                            </label>
                        )}
                        {canToggle && (
                            <div className="inline-flex rounded border overflow-hidden text-xs" role="group">
                                {(['frozen', 'current'] as const).map(v => (
                                    <button key={v} onClick={() => setView(v)}
                                            className={`px-2.5 py-1.5 font-medium transition-colors ${
                                                view === v
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                                        {t(v === 'frozen' ? 'entries.viewFrozen' : 'entries.viewCurrent')}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {view === 'frozen' && reconstructed && (
                    <div className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-900">
                        <FileClock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span>{t('entries.reconstructedBanner')}</span>
                    </div>
                )}

                {view === 'frozen' && !loading && !snapshotAvailable && (
                    <div className="px-4 py-2.5 border-b bg-gray-50 text-xs text-gray-500">
                        {t('entries.unavailableFrozen')}
                    </div>
                )}

                {delta?.available && (delta.added.length + delta.changed.length + delta.removed.length > 0) && (
                    <div className="flex flex-wrap items-center gap-3 border-b bg-blue-50/40 px-4 py-2 text-xs text-gray-700">
                        <span>{t('entries.diffIntro')}</span>
                        {delta.added.length > 0 && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-green-800">
                                {delta.added.length} {t('delta.added')}
                            </span>
                        )}
                        {delta.changed.length > 0 && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-amber-800">
                                {delta.changed.length} {t('delta.changed')}
                            </span>
                        )}
                        {delta.removed.length > 0 && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-red-800">
                                {delta.removed.length} {t('delta.removed')}
                            </span>
                        )}
                    </div>
                )}

                {activeCount > 0 && (
                    <div className="flex items-center justify-between px-4 py-1.5 border-b bg-amber-50 text-xs text-amber-900">
                        <span>{t('filter.active', { count: activeCount })}</span>
                        <button onClick={clearAll} className="text-blue-600 hover:text-blue-800 font-medium">
                            {t('filter.clearAll')}
                        </button>
                    </div>
                )}

                {/* The sticky header needs a bounded scroll container or it does not stick. */}
                <div className="overflow-auto" style={{ maxHeight: '70vh' }}>
                    {loading && (
                        <div className="p-12 text-center text-gray-500">
                            <Loader2 className="h-5 w-5 animate-spin inline mr-2" />{t('page.loading')}
                        </div>
                    )}
                    {!loading && error && <div className="p-6 text-red-600 text-sm">{error}</div>}
                    {!loading && !error && rows.length === 0 && (
                        <div className="p-8 text-center text-gray-500">{t('entries.empty')}</div>
                    )}
                    {!loading && !error && rows.length > 0 && filtered.length === 0 && (
                        <div className="p-8 text-center text-gray-500">{t('filter.active', { count: activeCount })}</div>
                    )}
                    {!loading && !error && filtered.length > 0 && (
                        <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                            <colgroup>
                                {COLUMNS.map(c => <col key={c.key} style={{ width: widthOf(c.key) }} />)}
                                <col style={{ width: 130 }} />
                                <col style={{ width: 110 }} />
                            </colgroup>
                            <thead>
                                <tr className="border-b">
                                    {COLUMNS.map(c => (
                                        <SortableHeader
                                            key={c.key}
                                            label={t(c.labelKey)}
                                            sortKey={c.key}
                                            sort={sort}
                                            onSort={onSort}
                                            align={c.align}
                                            onResizeStart={x => startResize(c.key, x)}
                                            onResetWidth={() => resetWidth(c.key)}
                                            isResizing={resizingKey === c.key}
                                            filter={
                                                <ColumnFilter
                                                    label={t(c.labelKey)}
                                                    values={uniqueValuesFor(c.key)}
                                                    state={filters[c.key]}
                                                    onChange={s => setColumnFilter(c.key, s)}
                                                    numeric={NUMERIC.includes(c.key)}
                                                    active={isFiltered(c.key)}
                                                    labels={filterLabels}
                                                />
                                            }
                                        />
                                    ))}
                                    <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-left text-xs font-medium text-gray-500">
                                        {t('drill.document')}
                                    </th>
                                    <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-left text-xs font-medium text-gray-500">
                                        {t('excl.column')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {sorted.map(r => {
                                    const changedInfo = deltaByEntry.changed.get(r.entryNo);
                                    const isAdded = view === 'current' && deltaByEntry.added.has(r.entryNo);
                                    const isRemoved = view === 'frozen' && deltaByEntry.removed.has(r.entryNo);
                                    const isChanged = !!changedInfo;
                                    const rowClass = isRemoved
                                        ? 'bg-red-50'
                                        : isAdded
                                            ? 'bg-green-50'
                                            : isChanged
                                                ? 'bg-amber-50'
                                                : r.kind === 'Ausgleich' ? 'bg-gray-50' : 'hover:bg-gray-50';
                                    return (
                                        <tr key={r.entryNo}
                                            title={exclusionTitle(r)}
                                            className={`${rowClass} ${exclusionRowClass(r, year, month)}`}>
                                            <td className="px-3 py-1.5 text-gray-500 tabular-nums">{r.entryNo}</td>
                                            <td className="px-3 py-1.5 whitespace-nowrap">{r.reportingDate}</td>
                                            <td className="px-3 py-1.5 whitespace-nowrap text-gray-600"
                                                title={r.postingDate ? undefined : t('entries.notCaptured')}>
                                                {r.postingDate ?? '—'}
                                            </td>
                                            <td className="px-3 py-1.5 font-medium truncate" title={r.documentNo}>
                                                {r.documentNo || '—'}
                                            </td>
                                            <td className="px-3 py-1.5 text-gray-600 truncate">
                                                {r.documentType || '—'}
                                            </td>
                                            <td className="px-3 py-1.5 truncate"
                                                title={[r.counterpartyName, r.counterpartyNo].filter(Boolean).join(' · ')}>
                                                {r.counterpartyName ?? <span className="text-gray-400">—</span>}
                                                {r.counterpartyNo && (
                                                    <span className="ml-1 text-xs text-gray-400">{r.counterpartyNo}</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-1.5 text-gray-600 truncate">{r.counterpartyVatId || '—'}</td>
                                            <td className="px-3 py-1.5 text-gray-600"
                                                title={r.countryRegionCode ? undefined : t('entries.notCaptured')}>
                                                {r.countryRegionCode || '—'}
                                            </td>
                                            <td className="px-3 py-1.5 text-gray-600 truncate"
                                                title={`${r.vatBusPostingGroup} / ${r.vatProdPostingGroup}`}>
                                                {r.vatBusPostingGroup} / {r.vatProdPostingGroup}
                                            </td>
                                            <td className="px-3 py-1.5 font-mono">{r.kennzahl || '—'}</td>
                                            <td className="px-3 py-1.5 text-xs">
                                                {r.disposition === 'mapped' ? (
                                                    <span className="text-gray-500">{r.disposition}</span>
                                                ) : r.disposition ? (
                                                    <span className="inline-flex items-center gap-0.5 text-red-600">
                                                        <AlertTriangle className="h-3 w-3" />{r.disposition}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400">—</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-1.5 text-right tabular-nums"
                                                title={isChanged && changedInfo?.snapshotBase !== undefined
                                                    ? `${view === 'frozen' ? t('entries.viewCurrent') : t('entries.viewFrozen')}: ${money(view === 'frozen' ? changedInfo.currentBase ?? null : changedInfo.snapshotBase ?? null)}`
                                                    : undefined}>
                                                {money(r.baseLocal)}
                                            </td>
                                            <td className="px-3 py-1.5 text-right tabular-nums"
                                                title={isChanged && changedInfo?.snapshotTax !== undefined
                                                    ? `${view === 'frozen' ? t('entries.viewCurrent') : t('entries.viewFrozen')}: ${money(view === 'frozen' ? changedInfo.currentTax ?? null : changedInfo.snapshotTax ?? null)}`
                                                    : undefined}>
                                                {money(r.taxLocal)}
                                            </td>
                                            {/* Read-only here: corrections are made in the drill modal. */}
                                            <td className="px-3 py-1.5 text-right"><ReportingAmountCell row={r} field="base" editable={false} /></td>
                                            <td className="px-3 py-1.5 text-right"><ReportingAmountCell row={r} field="tax" editable={false} /></td>
                                            <td className="px-3 py-1.5 text-xs text-gray-500">
                                                {r.kind === 'Ausgleich' ? t('drill.settlement') : '—'}
                                            </td>
                                            <td className="px-3 py-1.5">
                                                <DocumentLinks row={r} company={company} />
                                            </td>
                                            {/* The frozen view is a record, not a worklist: a
                                                snapshot only ever held what was declared, and
                                                offering to change it would suggest it can be. */}
                                            <td className="px-3 py-1.5 no-underline">
                                                {view === 'current' ? (
                                                    <ExclusionActionCell row={r} canEdit={canEdit}
                                                                         periodLocked={periodLocked}
                                                                         onAct={openAction} />
                                                ) : (
                                                    <span className="text-xs text-gray-300">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot className="bg-gray-50 border-t font-medium sticky bottom-0">
                                <tr>
                                    <td className="px-3 py-2" colSpan={11}>
                                        {activeCount > 0
                                            ? t('drill.filteredCount', { shown: filtered.length, total: rows.length })
                                            : t('drill.count', { count: total })}
                                        {truncated && ` ${t('drill.showingFirst', { count: rows.length })}`}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">{money(sumBaseLocal)}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{money(sumTaxLocal)}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                        {anyReporting ? money(sumBaseReporting) : '—'}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                        {anyReporting ? money(sumTaxReporting) : '—'}
                                    </td>
                                    <td />
                                    <td />
                                    <td />
                                </tr>
                            </tfoot>
                        </table>
                    )}
                </div>
            </div>

            {dialog && (
                <EntryExclusionDialog
                    action={dialog.action}
                    company={company}
                    year={year}
                    month={month}
                    target={dialog.target}
                    onClose={() => setDialog(null)}
                    onDone={() => { setDialog(null); setReloadKey(k => k + 1); }}
                />
            )}
        </div>
    );
}

function DocumentLinks({ row, company }: { row: VatEntryRow; company: VatCompany }) {
    const { t } = useVatLang();
    const bcUrl = getBcPostedDocumentUrl(company, row.documentNo, row.bcDocumentKind);

    return (
        <span className="inline-flex items-center gap-2" onClick={e => e.stopPropagation()}>
            {row.docuWareUrl ? (
                <a href={row.docuWareUrl} target="_blank" rel="noopener noreferrer"
                   title={row.docuWareUrlVerified
                       ? t('drill.scanTitle', { confidence: row.docuWareMatchConfidence ?? '?' })
                       : t('drill.scanUnverified')}
                   className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800">
                    <FileText className="h-3.5 w-3.5" />
                    <span className="text-xs">{t('drill.scan')}</span>
                    {!row.docuWareUrlVerified && <span className="text-xs text-amber-600">?</span>}
                </a>
            ) : (
                <span className="text-xs text-gray-400"
                      title={row.kind === 'Verkauf'
                          ? t('drill.salesNotScanned')
                          : t('drill.noScanLinked')}>
                    {t('drill.noScan')}
                </span>
            )}
            {bcUrl && (
                <a href={bcUrl} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                   title={bcLinkHint(row.bcDocumentKind)}>
                    <ExternalLink className="h-3.5 w-3.5" />
                    <span className="text-xs">BC</span>
                </a>
            )}
        </span>
    );
}
